import React from 'react'
import { Box } from '@hub/box'
import {
  HubStyleObject,
  StandardColors,
  useToken,
  useTheme,
  css,
  mapRules,
} from '@hub/design-system-base'
import { H3 } from '@hub/heading'
import { Link } from '@hub/link'
import { Stack } from '@hub/stack'
import { Text } from '@hub/text'
import { IncludeDirectLogo } from '@hub/icon/src/with-direct-logo'

import {
  SGPageTemplateProduct,
  SGPageTemplateImage,
} from '../../../../src/types/page-templates'
import { CloudinaryImage } from '../cloudinary-image'
import { ProductCard } from '../product-card'
import { Reel } from './reel'
interface CollectionDeckProps {
  title: string
  text?: string
  url: string
  image: SGPageTemplateImage
  products: SGPageTemplateProduct[]
}

interface ProductBoxProps {
  product: SGPageTemplateProduct
  sx?: HubStyleObject
  isFirst: boolean
  isLast: boolean
  backgroundColor: StandardColors
}

const ProductBox: React.FC<ProductBoxProps> = ({
  product,
  sx = {},
  isFirst,
  isLast,
  backgroundColor,
}) => {
  const theme = useTheme()
  const [paddingSpace] = useToken('space', ['spacing-sm'])

  // We need to account for the spacing on the left/right of the first/last
  // items, which is used for the white border effect around the reel. If we
  // don't modify the minWidth, that extra padding (which isn't present on any
  // other items in the Reel) will result in the width of the image inside the
  // content box of <ProductCard> to be smaller (because image width + padding
  // of first/last items == image width of middle items).
  // Because sx.minWidth can be a responsive value, we have to map the rules
  // and set the value to a `calc` to ensure everything is type safe and
  // responsive safe.
  const adjustedFlexProperties = mapRules(
    css({
      minWidth: sx.minWidth ?? 0,
      maxWidth: sx.maxWidth ?? 0,
      flexBasis: sx.flexBasis ?? 0,
    })(theme),
    value => `calc(${value} + ${paddingSpace})`
  )

  return (
    <ProductCard
      sx={{
        zIndex: 'elevatedContent',
        backgroundColor,
        flexGrow: 1,
        ...sx,
        // To give a "border" effect, we put padding around each item, which
        // allows the background color of the containing Reel to show through.
        paddingY: 'spacing-sm',
        ...(isFirst && {
          paddingLeft: 'spacing-sm',
          ...adjustedFlexProperties,
        }),
        ...(isLast && {
          paddingRight: 'spacing-sm',
          ...adjustedFlexProperties,
        }),
        // For in-between elements, they have margin which doesn't get filled
        // with the background color, so we have to "extend" it by using an
        // outline
        ...(!isLast &&
          !isFirst && {
            outlineStyle: 'solid',
            outlineColor: backgroundColor,
            outlineWidth: paddingSpace,
          }),
      }}
      product={product}
    />
  )
}

const ProductBoxes: React.FC<{
  products: CollectionDeckProps['products']
  sx?: HubStyleObject
}> = ({ products, sx = {} }) => (
  <React.Fragment>
    {products.map((product, index) => (
      <ProductBox
        key={product.variantGroupCode}
        product={product}
        isFirst={index === 0}
        isLast={index === products.length - 1}
        backgroundColor="white"
        sx={sx}
      />
    ))}
  </React.Fragment>
)

export const CollectionDeck: React.FC<CollectionDeckProps> = ({
  title,
  text,
  url,
  image,
  products,
}) => {
  const [
    reelPadding,
    productsMobileNegativeMarginTop,
    productsMobileNegativeMarginLeft,
    productsMobileNegativeMarginRight,
    productsNonMobileNegativeMaxMarginLeft,
    scrollPaddingInlineStart,
  ] = useToken('space', [
    'spacing-sm',
    'spacing-md',
    'spacing-md',
    'spacing-md',
    'spacing-lg',
    'spacing-xl',
  ])

  const [mobileBreakpoint, tabletBreakpoint] = useToken('breakpoints', [
    'sm',
    'md',
  ])

  const reelMarginOffsetDesktop = `max(-50%, calc(-${productsNonMobileNegativeMaxMarginLeft} - ${reelPadding}))`

  // Convert the breakpoints into an absolute value so it can be used as the
  // divisor in a calc (at least one side of a division must be unitless)
  const tabletWidthRems = (
    parseFloat(tabletBreakpoint) - parseFloat(mobileBreakpoint)
  ).toString()

  // How far is the current viewport width into the tablet breakpoint, in
  // multiples of rems? NOTE: assumes 1rem = 16px
  const distanceIntoTabletRems = `calc(calc(100vw - ${mobileBreakpoint}) / 16)`
  const distanceIntoTabletInverseRatio = `calc(1px - calc(${distanceIntoTabletRems} / ${tabletWidthRems}))`

  // How far should the Reel travel left over the course of the tablet
  // breakpoint?
  // Must be unitless, but interpreted as pixels
  const distanceForReelToTravelOnTabletSize = '100'

  const reelMarginOffsetTablet = `calc(${reelMarginOffsetDesktop} - calc(${distanceForReelToTravelOnTabletSize} * ${distanceIntoTabletInverseRatio}))`

  const CONTAINER_WIDTH = '100%'
  const MIN_LEFT_COLUMN_WIDTH = 'var(--chakra-sizes-size-lg)'
  // Left column is either 42%, or a maximum of half the screen height to
  // support landscape phone / tablet sizes
  const MAX_LEFT_COLUMN_WIDTH = 'min(42%, 430px)'
  const MIN_RIGHT_COLUMN_WIDTH = `calc(${CONTAINER_WIDTH} - ${MAX_LEFT_COLUMN_WIDTH})`

  // prettier-ignore
  const rightColumnMaxWidth =
    'calc(' +
      `${CONTAINER_WIDTH}` +
      ' - max(' +
        `${MAX_LEFT_COLUMN_WIDTH},` +
        `${MIN_LEFT_COLUMN_WIDTH}` +
      ')' +
    ')'

  return (
    <Box
      sx={{
        display: ['block', 'flow-root'],
        margin: '0 auto',
      }}
    >
      <Stack
        sx={{
          float: [null, 'right'],
          minWidth: MIN_RIGHT_COLUMN_WIDTH,
          maxWidth: [null, rightColumnMaxWidth],
          paddingLeft: [null, 'spacing-md'],
          marginBottom: ['spacing-sm', null],
        }}
        gap="spacing-xs"
      >
        <Stack gap="spacing-none">
          <H3 align={['center', 'left', 'left']}>
            <IncludeDirectLogo title={title} />
          </H3>
          <Text color="quartz" noOfLines={2} align={['center', 'left', 'left']}>
            {text}
          </Text>
        </Stack>
        <Text align={['center', 'left', 'left']}>
          <Link hasUnderline href={url}>
            Shop now
          </Link>
        </Text>
      </Stack>
      <CloudinaryImage
        imageSetOrImage={image}
        ratio="1"
        sx={{
          float: [null, 'left'],
          minWidth: 'size-lg',
          maxWidth: [null, MAX_LEFT_COLUMN_WIDTH],
          width: 'size-full',
        }}
      />
      <Box
        sx={{
          zIndex: 'elevatedContent',
          float: [null, 'right'],
          minWidth: MIN_RIGHT_COLUMN_WIDTH,
          maxWidth: [null, rightColumnMaxWidth],
          width: 'size-full',
        }}
      >
        <Reel
          snap
          minWidth="size-lg"
          maxWidth="size-xl"
          shouldWrapChildren={false}
          gap="spacing-sm"
          sx={{
            // Ensure background sits above adjacent image
            position: 'relative',
            backgroundColor: 'white',
            // Only add background to the content, not the padding
            backgroundClip: 'content-box',
            paddingLeft: ['spacing-xl', 'spacing-0'],
            scrollPaddingInlineStart: [
              scrollPaddingInlineStart,
              null,
              'initial',
            ],
            marginTop: [`-${productsMobileNegativeMarginTop}`, 0],
            marginLeft: [
              `-${productsMobileNegativeMarginLeft}`,
              reelMarginOffsetTablet,
              reelMarginOffsetDesktop,
            ],
            marginRight: [
              `-${productsMobileNegativeMarginRight}`,
              `-${productsMobileNegativeMarginRight}`,
              'initial',
            ],
            display: 'flex',
          }}
        >
          <ProductBoxes products={products} />
        </Reel>
      </Box>
    </Box>
  )
}
